import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { TERVEZESI_FELADATOK, MERETEK } from "../mock/mock";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-11 sm:h-12 rounded-lg bg-transparent border-white/40 text-white placeholder:text-white/50 focus-visible:ring-[var(--accent)] focus-visible:border-[var(--accent)]";

const ContactSection = () => {
  const [form, setForm] = useState({
    nev: "",
    telefon: "",
    email: "",
    feladat: "Lakóház",
    meret: "100m²>",
    adatkezeles: false,
  });
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const navigate = useNavigate();

  const handleFirstFocus = () => {
    if (!started) {
      setStarted(true);
      track("form_start");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nev.trim() || !form.telefon.trim()) {
      toast.error("Kérjük, töltsd ki a csillaggal jelölt mezőket (Név, Telefon).");
      return;
    }
    if (!form.adatkezeles) {
      toast.error("Kérjük, fogadd el az Adatkezelési feltételeket.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        nev: form.nev.trim(),
        telefon: form.telefon.trim(),
        email: form.email.trim(),
        feladat: form.feladat,
        meret: form.meret,
      });
      setForm({ nev: "", telefon: "", email: "", feladat: "Lakóház", meret: "100m²>", adatkezeles: false });
      track("form_submit");
      navigate("/koszonjuk");
    } catch {
      toast.error("Hiba történt, kérjük próbáld újra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kapcsolat" className="px-2.5 pb-2.5">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-2.5">
        {/* Bal oldali narancssárga kártya */}
        <div className="rounded-2xl bg-[var(--accent)] p-6 sm:p-12 flex flex-col justify-between min-h-0 lg:min-h-[420px]">
          <div>
            <p className="text-black text-sm font-bold mb-2 sm:mb-4">Kapcsolat</p>
            <h2 className="text-black text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] heading-font">
              Kezdjük egy<br />díjmentes<br />konzultációval
            </h2>
          </div>
          <p className="text-black/90 font-semibold text-sm sm:text-base leading-5 sm:leading-6 mt-5 sm:mt-12 max-w-sm">
            Az üzenet elküldése után 1 munkanapon belül felvesszük veled a
            kapcsolatot, és egyeztetünk egy díjmentes konzultációt.
          </p>
        </div>

        {/* Jobb oldali űrlap kártya */}
        <div className="rounded-2xl bg-[var(--form-card)] p-5 sm:p-12">
          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5 sm:gap-5 max-w-xl ml-auto">
            <div>
              <label className="text-xs text-white/80 font-semibold">* Név</label>
              <Input
                value={form.nev}
                onChange={(e) => set("nev", e.target.value)}
                onFocus={handleFirstFocus}
                placeholder="Név"
                className={`mt-1 ${inputCls}`}
              />
            </div>
            <div>
              <label className="text-xs text-white/80 font-semibold">* Telefon</label>
              <Input
                value={form.telefon}
                onChange={(e) => set("telefon", e.target.value)}
                placeholder="Telefon"
                className={`mt-1 ${inputCls}`}
              />
            </div>
            <div>
              <label className="text-xs text-white/80 font-semibold">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="Email"
                className={`mt-1 ${inputCls}`}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-white/80 font-semibold">* Tervezési feladat</label>
                <Select value={form.feladat} onValueChange={(v) => set("feladat", v)}>
                  <SelectTrigger className={`mt-1 w-full ${inputCls}`}>
                    <SelectValue placeholder="Tervezési feladat" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c1c1c] text-white border-white/20">
                    {TERVEZESI_FELADATOK.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-[var(--accent)] focus:text-black">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-white/80 font-semibold">* Méret</label>
                <Select value={form.meret} onValueChange={(v) => set("meret", v)}>
                  <SelectTrigger className={`mt-1 w-full ${inputCls}`}>
                    <SelectValue placeholder="Méret" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c1c1c] text-white border-white/20">
                    {MERETEK.map((m) => (
                      <SelectItem key={m} value={m} className="focus:bg-[var(--accent)] focus:text-black">
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <Checkbox
                checked={form.adatkezeles}
                onCheckedChange={(v) => set("adatkezeles", !!v)}
                className="border-white/60 data-[state=checked]:bg-[var(--accent)] data-[state=checked]:border-[var(--accent)]"
              />
              <span className="text-sm text-white/90">
                * Elfogadom az{" "}
                <a
                  href="https://www.newideaarchitect.com/accessibility-statement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline underline-offset-2 hover:text-[var(--accent)] transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  Adatkezelési
                </a>{" "}
                feltételeket.
              </span>
            </label>

            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3.5 bg-white text-black text-sm font-bold rounded-md hover:bg-[var(--accent)] transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? "KÜLDÉS..." : "DíJMENTES KONZULTÁCIÓ"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
